import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { lookupZip } from "@/lib/api";

// Reusable address sub-block: Street + Unit + City + State + ZIP.
// Used by Clients.jsx's CustomerInfoPanel and the EditClientDialog.
//
// Caller passes a single `values` object and an `onChange(partial)`
// callback — we never own the state. ZIP auto-lookup fires on
// city/state blur when ZIP is empty and city+state are populated.
//
// `compact` (default true) lays out the fields in a 4-column grid
// matching the side-sheet panel layout. Set to `false` to stack
// vertically inside narrower dialogs.
//
// `readOnly` flips every field into the locked styling (gray bg,
// readOnly attr) — used by the customer side-sheet's master lock.
//
// Extracted from Clients.jsx (Feb 2026, iter177) for DRY use across
// EditClientDialog.
export function AddressFields({
  values,
  onChange,
  readOnly = false,
  compact = true,
  testIdPrefix = "input-detail",
  showLabels = false,
}) {
  // Auto-lookup ZIP when city + state are populated but ZIP is empty.
  const maybeLookupZip = () => {
    if ((values.city || "").length >= 3 && (values.state || "").length >= 2 && !values.zip_code) {
      lookupZip(values.city, values.state)
        .then((r) => {
          if (r.data.zip_code && !values.zip_code) {
            onChange({ zip_code: r.data.zip_code });
          }
        })
        .catch(() => {});
    }
  };

  const lockedClass = readOnly
    ? "bg-slate-100 text-slate-600 cursor-not-allowed border-slate-200"
    : "";

  if (compact) {
    return (
      <div className="grid grid-cols-2 sm:grid-cols-[2fr_1fr_70px_100px] gap-2">
        <div className="col-span-2 sm:col-span-1">
          <div className="grid grid-cols-3 gap-2">
            <Input
              value={values.address || ""}
              onChange={(e) => onChange({ address: e.target.value })}
              readOnly={readOnly}
              placeholder="Street address"
              className={`h-8 text-sm w-full col-span-2 ${lockedClass}`}
              autoComplete="off"
              data-testid={`${testIdPrefix}-address`}
            />
            <Input
              value={values.address_line_2 || ""}
              onChange={(e) => onChange({ address_line_2: e.target.value })}
              readOnly={readOnly}
              placeholder="Unit"
              className={`h-8 text-sm w-full ${lockedClass}`}
              autoComplete="off"
              data-testid={`${testIdPrefix}-address-line-2`}
            />
          </div>
        </div>
        <Input
          value={values.city || ""}
          onChange={(e) => onChange({ city: e.target.value })}
          readOnly={readOnly}
          onBlur={maybeLookupZip}
          placeholder="City"
          className={`h-8 text-sm ${lockedClass}`}
          autoComplete="off"
          data-testid={`${testIdPrefix}-city`}
        />
        <Input
          value={values.state || ""}
          onChange={(e) => onChange({ state: e.target.value.toUpperCase() })}
          readOnly={readOnly}
          onBlur={maybeLookupZip}
          placeholder="State"
          maxLength={2}
          className={`h-8 text-sm ${lockedClass}`}
          autoComplete="off"
          data-testid={`${testIdPrefix}-state`}
        />
        <Input
          value={values.zip_code || ""}
          onChange={(e) => onChange({ zip_code: e.target.value })}
          readOnly={readOnly}
          placeholder="ZIP"
          maxLength={10}
          className={`h-8 text-sm ${lockedClass}`}
          autoComplete="off"
          data-testid={`${testIdPrefix}-zip`}
        />
      </div>
    );
  }

  // Stacked layout for dialogs (EditClientDialog uses this).
  return (
    <div className="grid grid-cols-2 gap-3">
      <div className="col-span-2">
        {showLabels && <Label className="text-xs">Address</Label>}
        <Input
          value={values.address || ""}
          onChange={(e) => onChange({ address: e.target.value })}
          placeholder="123 Main St"
          data-testid={`${testIdPrefix}-address`}
        />
      </div>
      <div>
        {showLabels && <Label className="text-xs">City</Label>}
        <Input
          value={values.city || ""}
          onChange={(e) => onChange({ city: e.target.value })}
          onBlur={maybeLookupZip}
          placeholder="Denver"
          data-testid={`${testIdPrefix}-city`}
        />
      </div>
      <div className="grid grid-cols-2 gap-2">
        <div>
          {showLabels && <Label className="text-xs">State</Label>}
          <Input
            value={values.state || ""}
            onChange={(e) => onChange({ state: e.target.value.toUpperCase() })}
            onBlur={maybeLookupZip}
            placeholder="CO"
            maxLength={2}
            data-testid={`${testIdPrefix}-state`}
          />
        </div>
        <div>
          {showLabels && <Label className="text-xs">ZIP</Label>}
          <Input
            value={values.zip_code || ""}
            onChange={(e) => onChange({ zip_code: e.target.value })}
            placeholder="80202"
            maxLength={10}
            data-testid={`${testIdPrefix}-zip`}
          />
        </div>
      </div>
    </div>
  );
}
